import type { AxiosResponse } from 'axios'
import type { ErrorModel } from '@/api/model/errorModel'
import type { BulkAPIResponse } from '@/types'

import { errorMessage } from './util'
import usePageMessage from '@/hooks/exception/usePageMessage'
import { getMessage } from './message'
import { ResponseMessageTypeEnum } from '@/enums'

/**
 * @description API 例外処理
 * @param response
 * @returns レスポンス
 */
export function handleAPIException<T = any, D = any>(
  response: AxiosResponse<T, D>
): AxiosResponse<T, D> {
  const { clearPageMessages } = usePageMessage()

  if (response.status === 200) {
    clearPageMessages()
    return response
  }

  const result = response.data as unknown as ErrorModel

  if (!response?.status || !result?.message) {
    errorMessage(getMessage('EB0002'), 'error')
  }

  let errors: string[] = []
  if (result.error_code.length > 0) {
    //error_code値がある場合、メッセージは一条しかないので、直接メッセージを返す。
    errors = [result.message]
  } else {
    //error_code値がない場合、メッセージは複数ので、複数に処理してメッセージを返す。
    errors = formatAPIException(result.message)
  }

  for (const element of errors) {
    setTimeout((): void => {
      errorMessage(element, 'error')
    }, 100)
  }

  throw new Error(`${result.error_code}: ${errors}`)
}

export function formatAPIException(exception: string): string[] {
  const messageList: string[] =
    exception
      ?.split('\n')
      .filter((message: string): boolean => message !== ' ')
      .map<string>((message: string) => message.split(':')[1] ?? message) ?? []

  return Array.from(new Set(messageList))
}

/**
 * @description 一括 API 異常処理
 * @param response レスポンス
 * @param businessExceptionCallback 業務例外処理コールバック
 * @returns [レスポンス, 成功か否か]
 * */
export function handleBulkAPIException<T = any, D = any>(
  response: AxiosResponse<BulkAPIResponse<T>, D>,
  businessExceptionCallback?: (
    response: AxiosResponse<BulkAPIResponse<T>, D>
  ) => Promise<void> | void
): [AxiosResponse<BulkAPIResponse<T>, D>, boolean] {
  const { clearPageMessages } = usePageMessage()

  if (response.status === 200) {
    clearPageMessages()
  }

  let succeed: boolean = false

  if (response.status !== 200) {
    errorMessage(getMessage('EF007006'), 'error')
  } else if (response.data.ng_count) {
    if (typeof businessExceptionCallback === 'function') {
      if (businessExceptionCallback.toString().includes('async')) {
        businessExceptionCallback(response)?.then((): void => {
          errorMessage(getMessage('EF007005'), 'success')
        })
      } else {
        businessExceptionCallback(response)
        errorMessage(getMessage('EF007005'), 'success')
      }
    } else {
      errorMessage(getMessage('EF007005'), 'success')
    }
  } else {
    errorMessage(getMessage('EF007004'), 'success')
    succeed = true
  }

  return [response, succeed]
}

/**
 * @description 通常の異常処理
 * @param error 異常文又は異常文を含むオブジェクト
 */
export function handleExceptionNormal(error: unknown | string | ErrorModel) {
  const textError = error as string
  const objectError = error as ErrorModel

  if (objectError.message) {
    const errors = formatAPIException(objectError.message)

    for (const element of errors) {
      setTimeout((): void => {
        errorMessage(element, 'error')
      }, 100)
    }
  } else {
    errorMessage(textError, ResponseMessageTypeEnum.ERROR)
  }

  console.error(
    new Error(
      objectError.message
        ? `${objectError.error_code}: ${objectError.message}`
        : textError
    )
  )
}
